/**
 * The message bubble's fixed geometry, in one place (GitLab #1710).
 *
 * `MessageRow` draws the bubble from these numbers and `MessageBody` sizes
 * the pictures inside it from the same numbers, so the two cannot drift. They
 * live apart from `MessageRow` because `MessageRow` imports `MessageBody`,
 * and a constant both need cannot sit in either without a cycle.
 *
 * ⚠️ WHY A PICTURE'S WIDTH IS DERIVED HERE AND NOT MEASURED (#1710). On
 * native, `RichHtmlView` used to measure its own width with `onLayout` and
 * hand that to `react-native-render-html` as `contentWidth`, which is the
 * ceiling RNRH scales an `<img>` into. Inside a shrink-to-fit bubble the
 * picture's width IS the bubble's width, so the measurement fed itself: the
 * ticket video shows a portrait photo's box flipping between its correct
 * 120×160 and a ~50pt cover-crop every 3-5 frames at 60 fps, the bubble
 * resizing with it, with nobody touching the screen. Measured from the video
 * and reasoned from the code: the measured width is the only state in that
 * chain that can move a picture's width, so the bubble now passes a width
 * that depends on nothing it contains. What produces the ~50 is NOT explained
 * by RNRH's own state machine (its loading box is 100×100 and it resets only
 * on a `src` change); the walk is the confirmation, not this paragraph.
 *
 * The derivation is the bubble's own: the list pads its content
 * (`LIST_PADDING_H` each side), the message column is capped at
 * `BUBBLE_MAX_WIDTH_FRACTION` of that row, an other-side message gives up
 * the avatar column, and the bubble pads horizontally. What is left is capped
 * at `RICH_HTML_IMG_MAX_WIDTH`, so on any phone of 375pt or more the ceiling
 * is exactly the 240 it was; a 320pt window (iPhone SE, Display Zoom, iPad
 * Slide Over) gets 209. ONE width serves both sides: an own-side bubble has
 * 18 more than this, and spending it would make the two sides' pictures
 * differ for no reader-visible reason. The window is the right denominator on
 * native because the panel is full-screen there: the resizable dock exists on
 * web only (`DOCK_MIN_VIEWPORT_WIDTH`), and web renders rich text through the
 * DOM, never through RNRH.
 *
 * ⚠️ `react-test-renderer` performs no layout. The pins over this module and
 * its callers assert the DECLARED width; that the picture holds still is
 * walk-verified (correspondence.md, "A ceiling is not a width").
 */
import { spacing } from '../../theme/spacing';
import { RICH_HTML_IMG_MAX_WIDTH } from '../richText/richHtmlVisualContract';

/**
 * The list's horizontal padding, each side (`MessageList`'s `bodyContent`).
 * It is a term in the picture width, which is why it lives here and not
 * only in the list's stylesheet: the review found the derivation 21pt wide
 * at 320 because this number lived in another file.
 */
export const LIST_PADDING_H = spacing.md;

/** The message column's share of the row; the lean takes the rest. */
export const BUBBLE_MAX_WIDTH_FRACTION = 0.85;

/** The same share as a percentage style, for `MessageRow`'s column. */
export const BUBBLE_MAX_WIDTH = `${Math.round(
  BUBBLE_MAX_WIDTH_FRACTION * 100,
)}%` as const;

/** The bubble's horizontal padding, each side. */
export const BUBBLE_PADDING_H = spacing.md;

/**
 * The avatar column on the other side, and how far the disc OVERLAPS the
 * bubble (owner, 2026-09-23, quick-dev spec-1069-thread-avatar-top-left).
 * Measured off the Teams reference at 8x: a 24 disc whose right quarter, 6,
 * sits over the bubble's leading edge, tops level. So the bubble starts 18
 * in, not 24 + a gap. It was 24 + 8 = 32 before that 2026-09-23 change.
 */
export const THREAD_AVATAR_SIZE = 24;
const AVATAR_OVERLAP = 6;
export const AVATAR_COLUMN = THREAD_AVATAR_SIZE - AVATAR_OVERLAP;

/**
 * The widest an inline picture may be inside a bubble on a window this wide:
 * the narrower of RNRH's picture ceiling and the room an other-side bubble
 * actually has. Pure, so it is pinned by value (393 → 240, 375 → 240,
 * 360 → 240, 320 → 209) and never by layout.
 */
export const inlinePictureContentWidth = (windowWidth: number): number =>
  Math.max(
    1,
    Math.min(
      RICH_HTML_IMG_MAX_WIDTH,
      Math.floor(
        (windowWidth - 2 * LIST_PADDING_H) * BUBBLE_MAX_WIDTH_FRACTION,
      ) -
        AVATAR_COLUMN -
        2 * BUBBLE_PADDING_H,
    ),
  );
